import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LxpLayout } from "@/components/lexpractiq/LxpLayout";
import { listMyMentorSessions, updateMentorSession } from "@/lib/api/lxp-mentors.functions";

export const Route = createFileRoute("/_authenticated/lexpractiq/mentors/sessions")({
  head: () => ({ meta: [{ title: "My mentor sessions | SA LexPractiq" }] }),
  component: SessionsPage,
});

function SessionsPage() {
  const listFn = useServerFn(listMyMentorSessions);
  const updFn = useServerFn(updateMentorSession);
  const [data, setData] = useState<{ asStudent: any[]; asMentor: any[] }>({ asStudent: [], asMentor: [] });

  async function reload() { setData(await listFn()); }
  useEffect(() => { reload(); }, []);

  async function confirm(id: string) {
    try { await updFn({ data: { id, status: "confirmed" } }); toast.success("Confirmed"); await reload(); }
    catch (e: any) { toast.error(e?.message); }
  }
  async function complete(id: string) {
    try { await updFn({ data: { id, status: "completed" } }); toast.success("Marked complete"); await reload(); }
    catch (e: any) { toast.error(e?.message); }
  }

  return (
    <LxpLayout>
      <div className="lxp-container" style={{ padding: "2rem 1.25rem" }}>
        <h1>My mentor sessions</h1>

        <h3 style={{ marginTop: "1.5rem" }}>As a student</h3>
        {data.asStudent.length === 0 && <p className="lxp-muted">No requests yet.</p>}
        <div style={{ display: "grid", gap: "0.5rem" }}>
          {data.asStudent.map((s) => (
            <div key={s.id} className="lxp-card">
              <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
                <div>
                  <strong>{s.topic}</strong>
                  <div className="lxp-muted" style={{ fontSize: "0.8rem" }}>
                    Mentor: {s.mentor?.display_name} · Status: {s.status}
                  </div>
                </div>
                {s.meeting_url && <a href={s.meeting_url} target="_blank" rel="noreferrer" className="lxp-btn">Join</a>}
              </div>
            </div>
          ))}
        </div>

        <h3 style={{ marginTop: "1.5rem" }}>As a mentor</h3>
        {data.asMentor.length === 0 && <p className="lxp-muted">No mentee requests yet.</p>}
        <div style={{ display: "grid", gap: "0.5rem" }}>
          {data.asMentor.map((s) => (
            <div key={s.id} className="lxp-card">
              <strong>{s.topic}</strong>
              <div className="lxp-muted" style={{ fontSize: "0.8rem" }}>
                Status: {s.status} {s.scheduled_at ? `· ${new Date(s.scheduled_at).toLocaleString()}` : ""}
              </div>
              {s.student_notes && <p style={{ fontSize: "0.85rem", marginTop: 6 }}>{s.student_notes}</p>}
              <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
                {s.status === "requested" && <button className="lxp-btn" onClick={() => confirm(s.id)}>Confirm</button>}
                {s.status !== "completed" && <button className="lxp-btn-ghost" onClick={() => complete(s.id)}>Mark complete</button>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </LxpLayout>
  );
}
